import { Grid, Typography } from '@mui/material';
import { MprcContainer, MprcRequestErrorReport, MprcSpinnerIndicator, MprcText } from 'components';
import { CalendarDay, MprcCalendarPicker } from 'components/MprcCalendarPicker';
import { EnrollmentCalendarItemResponse, EnrollmentCalendarResponse, EnrollmentCalendarType } from 'generated/CoreApi_v1';
import { Formaters } from 'lib/Formaters';
import moment from 'moment';
import { useCoreApi } from 'providers/CoreApiProvider';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

/////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    classroomId: string | null;
    enrollmentId: string | null;
    isPreview: boolean;
}

/////////////////////////////////////////////////////////////////////////////////////

export const EnrollmentViewCalendar: React.FC<IProps> = ({ enrollmentId, classroomId, isPreview }) => {

    const api = useCoreApi();
    const { t } = useTranslation("lms");

    const [date, setDate] = useState<Date | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [viewError, setViewError] = useState<any>();
    const [calendarItems, setCalendarItems] = useState<EnrollmentCalendarResponse[]>([]);

    /////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        setLoading(true);
        api.v1.enrollment.getEnrollmentCalendar({
            enrollmentId: isPreview ? null : enrollmentId,
            classroomId: isPreview ? classroomId : null,
            timezoneId: moment.tz.guess(),
            date: date
        })
            .then(response => setCalendarItems(response.data))
            .catch(e => setViewError(e))
            .finally(() => setLoading(false));
    }, [date]);

    /////////////////////////////////////////////////////////////////////////////////////

    const BuildName = (item: EnrollmentCalendarItemResponse, finished: boolean): React.ReactNode => {
        switch (item.type) {
            case EnrollmentCalendarType.TrainingStart:
                return t("Student.EnrollmentView.Calendar.TrainingStartText");
            case EnrollmentCalendarType.TrainingFinalization:
                return t("Student.EnrollmentView.Calendar.TrainingFinalizationText");
            case EnrollmentCalendarType.TrainingExpirationDeadline:
                return t("Student.EnrollmentView.Calendar.TrainingExpirationDeadlineText");
            case EnrollmentCalendarType.TrainingReviewDeadline:
                return t("Student.EnrollmentView.Calendar.TrainingReviewDeadlineText");
            case EnrollmentCalendarType.TrainingValidityDeadline:
                return t("Student.EnrollmentView.Calendar.TrainingValidityDeadlineText");
            case EnrollmentCalendarType.TrainingCancelDeadline:
                return t("Student.EnrollmentView.Calendar.TrainingCancelDeadlineText");
            case EnrollmentCalendarType.DeliveryActivity:
                return <>{`${t("Student.EnrollmentView.Calendar.DeliveryActivityText")}: `}<b>{item.name}</b></>;
            case EnrollmentCalendarType.Meeting:
                return item.isMandatory
                    ? <>
                        <MprcText color={!finished ? "warning" : "inherit"}>
                            {t("Student.EnrollmentView.Calendar.MeetingMandatoryText")}
                        </MprcText>
                        <span>:&nbsp;</span>
                        <b>{item.name}</b>
                    </>
                    : <b>{item.name}</b>;
            default:
                return item.name;
        }
    };

    return <>
        <MprcRequestErrorReport error={viewError} />
        <Typography variant="h3" color="black">{t("Student.EnrollmentView.Calendar.Title")}</Typography>
        <Grid container direction="row" spacing={2}>
            <Grid>
                <MprcCalendarPicker
                    disableClickOnDates
                    enabledDates={calendarItems.map(item => ({ day: item.day, state: 'busy' } as CalendarDay))}
                    onChange={() => { }}
                    onMonthChange={(date) => setDate(date)}
                    selectedDate={null}
                />
            </Grid>
            <Grid>
                {loading ?
                    <MprcSpinnerIndicator />
                    : calendarItems.length == 0 ?
                        <Typography variant="caption">{t("Student.EnrollmentView.Calendar.NoRecordsFoundText")}</Typography>
                        : calendarItems.map((item, index) =>
                        (
                            <MprcContainer key={index}>
                                <Typography
                                    component="p"
                                    variant="captionHeader"
                                    fontWeight="bold"
                                    color={item.finished ? "muted.light" : "black"}
                                >
                                    {Formaters.dayMonth(item.day, { dayOfWeek: true, longNames: true })}
                                </Typography>
                                <Typography
                                    variant="caption"
                                    color={item.finished ? "muted.light" : "muted.main"}
                                >
                                    {item.items.length > 1 ?
                                        <ul style={{ margin: 0, paddingInlineStart: 15 }}>
                                            {item.items.map((event, i) => <li key={i}>{BuildName(event, item.finished)}</li>)}
                                        </ul>
                                        : BuildName(item.items[0], item.finished)
                                    }
                                </Typography>
                            </MprcContainer>
                        ))
                }
            </Grid>
        </Grid>
    </>;
};
